// Shared types for the UGC-ads wizard (Phase 2 + Phase 7 batch).
//
// The wizard walks the operator through attaching a UGC Media (or
// several — Phase 7) to a target and generating ads against a
// campaign of the matching kind. Backed by the Phase 1 assignment
// API (POST /api/media/:id/assign).
//
// MAX_UGCS_PER_BATCH — spec-mandated cap (Phase 7 W2). A 20-UGC ×
// N-concept batch is real money; Step 1 refuses to grow past this
// and Step 5 gates Generate on an explicit checkbox tick.
export const MAX_UGCS_PER_BATCH = 20;

export type WizardStepKey =
  | 'ugc'        // Step 1 — 2b: UGC picker (skipped when ?ugc=<id> is set)
  | 'target'     // Step 2 — 2c/d/e/f: attachment target (path + picker)
  | 'campaign'   // Step 3 — pick or +new
  | 'quickcreate'// Step 4 — inline quick-create for +new
  | 'confirm';   // Step 5 — preview + confirm

// The four operator attachment paths (mirrors the Phase 1 API's
// targetType enum plus the category variant). 'product' + 'category'
// are visually a radio inside the product-path picker (2f) but backed
// by different API targetType.
export type WizardPath = 'product' | 'category' | 'brand' | 'promotional';

// GET /api/media/:id/assignments response shape — used by the preview
// rail and by Step 2 (pre-checked existing attachments).
export type MediaAssignments = {
  ok:      true;
  media: {
    _id:            string;
    brandId:        string | null;
    source:         string | null;
    fileType:       string | null;
    fileUrl:        string | null;
    externalId:     string | null;
    socialPostType: string | null;
  };
  products: Array<{
    catalogProductId: string | null;
    title:            string | null;
    imageUrl:         string | null;
    price:            number | null;
    currency:         string | null;
    source:           'detect' | 'operator';
    outcome:          string | null;
    confidence:       number | null;
    assignedAt:       string | null;
    assignedBy:       string | null;
  }>;
  categories: Array<{
    categoryId:    string | null;
    name:          string | null;
    breadcrumbKey: string | null;
    source:        'detect' | 'operator';
    confidence:    number | null;
    assignedAt:    string | null;
    assignedBy:    string | null;
  }>;
  branding: { assignedAt: string; assignedBy: string | null } | null;
  promotional: {
    assignedAt: string;
    assignedBy: string | null;
    productIds: string[];
  } | null;
  counts: {
    operatorProducts:   number;
    detectProducts:     number;
    operatorCategories: number;
    detectCategories:   number;
    branding:           number;
    promotional:        number;
  };
};

export type WizardSelections = {
  // UGC-ads Phase 7 — batch. Multi-select in Step 1, capped at
  // MAX_UGCS_PER_BATCH. Length 1 is the Phase 2 single-UGC MVP path
  // (unchanged UX). Length >1 turns the whole wizard into batch mode:
  // one /api/ads/generate call per UGC at Confirm, each carrying its
  // own preferUgcMediaId so Phase 3's per-UGC seed cascade fires.
  mediaIds:     string[];
  path:         WizardPath;
  productIds:   string[];          // for path='product' — attached to EVERY selected UGC
  categoryIds:  string[];          // for path='category' — attached to EVERY selected UGC
  calloutProductIds: string[];     // optional callouts for path='promotional'
  campaignId:   string | null;
  // Inline quick-create staging — populated when operator picks +new
  // at Step 3 and lands on Step 4. Kind is inferred from `path`.
  newCampaign: {
    name:        string;
    description: string;
    offer:       string;   // promotional only
    endDate:     string;   // promotional only (ISO date)
  };
  // W2 confirmation checkbox — batch generation is real money. The
  // Confirm step requires an explicit checkbox tick before Generate
  // becomes clickable when mediaIds.length > 1. Auto-true for the
  // single-UGC case (no batch cost surprise there).
  batchAcknowledged: boolean;

  // Explicit multi-format selection — same billable model the Generate
  // Ads wizard uses. Each staticFormatKeys entry is its own billable
  // image generation; videoFormatKeys is server-clamped per platform
  // (Meta collapses any number of ticks to one master, Google emits
  // one per aspect). Sent to /api/ads/generate as preset:'explicit'.
  // Default single format (meta_feed_1_1 static + video) matches
  // Generate Ads' single-surface default so a UGC run doesn't quietly
  // fan out until the operator opts in.
  staticFormatKeys: string[];
  videoFormatKeys:  string[];
  // Target video length in seconds. Ignored when videoFormatKeys is
  // empty; enum-clamped inside the backend. Default 10s aligns with
  // Meta video's post-2026-08-11 default.
  videoDurationSec: number;
};
